import { ITelemetry } from '../../telemetry';
export const EXTERNAL_RESOURCE_NAME = 'EXTERNAL-JS-RESOURCES';
const INVALID_HOSTNAME = 'invalid hostname';

interface IResourceMetric {
  internalResource: {
    resources: Array<{
      resourceName: string;
      isInternal: boolean;
      transferSize: number | undefined;
      resourceLoadTime: number;
      hostname: string | undefined;
    }>;
  };
  combinedExternalResource: { resourceName: string; totalResourceCount: number };
}

type InternalResource = IResourceMetric['internalResource'];
type CombinedExternalResource = IResourceMetric['combinedExternalResource'];

interface ResourceMetadata {
  resourceName: string;
  isInternal: boolean;
  // use undefined instead of null so that when JSON.strinigify is used to report this, it will be removed from the final result
  transferSize: number | undefined;
  resourceLoadTime: number;
  originalName?: string;
  hostname?: string;
}

export const getJSResourcesMetrics = (telemetry: ITelemetry): IResourceMetric => {
  const result: IResourceMetric = {
    internalResource: { resources: [] },
    combinedExternalResource: { resourceName: '', totalResourceCount: 0 }
  };

  return (performance.getEntriesByType('resource') as PerformanceResourceTiming[])
    .filter(resource => resource.initiatorType === 'script')
    .map(resource => {
      // Example: resource.name is 'https://myapplications.microsoft.com/bundle/moderate-change-freq-vendors.8d4e8f51.js
      // we just want moderate-change-freq-vendors.8d4e8f51.js in the map function
      let resourceName;
      let hostname;
      try {
        const url = new URL(resource.name);
        hostname = url.hostname;
        resourceName = url.pathname.split('/').pop() ?? resource.name;
      } catch (error) {
        resourceName = resource.name;
        hostname = INVALID_HOSTNAME;
        telemetry.error('urlConstructionError', { error, resourceName: resource.name });
      }

      const isCached = isResourceCached(resource);
      const metadata: ResourceMetadata = {
        resourceName,
        originalName: resource.name,
        hostname: window.location.hostname,
        isInternal: hostname === window.location.hostname,
        resourceLoadTime: resource.duration,
        transferSize: isCached === undefined ? undefined : isCached ? 0 : resource.transferSize
      };
      return metadata;
    })
    .reduce((result, resourceMetadata) => {
      if (resourceMetadata.isInternal) {
        result.internalResource = updateInternalResourceMetric(resourceMetadata, result.internalResource);
      } else {
        result.combinedExternalResource = updateCombinedExternalResourceMetric(result.combinedExternalResource);
      }
      return result;
    }, result);
};

const isResourceCached = (resource: PerformanceResourceTiming): boolean | undefined => {
  const { transferSize, nextHopProtocol } = resource;
  if (transferSize !== undefined) {
    // transferSize is set to 0 when serving from cached with 200 Http status code
    // transferSize is set to 300 when serving from cached with 304 Http status code ( header size )
    // both cases are considered cached
    return transferSize === 0 || transferSize === 300;
  }
  // use this field to determine the cache status when transferSize is not available on this browser
  if (nextHopProtocol !== undefined) {
    return nextHopProtocol === '';
  }
  return undefined;
};

const updateInternalResourceMetric = (
  resourceMetadata: ResourceMetadata,
  internalResource: InternalResource
): InternalResource => {
  const { resourceName, resourceLoadTime, transferSize, hostname, isInternal } = resourceMetadata;
  internalResource.resources.push({ resourceName, resourceLoadTime, transferSize, hostname, isInternal });
  return internalResource;
};

const updateCombinedExternalResourceMetric = (currentExternalResource: {
  resourceName: string;
  totalResourceCount: number;
}): CombinedExternalResource => {
  return {
    resourceName: EXTERNAL_RESOURCE_NAME,
    totalResourceCount: currentExternalResource.totalResourceCount + 1
  };
};
